import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Eye,
  Building2,
  Calendar,
  CreditCard,
  TrendingUp,
  Banknote,
  Clock,
  DollarSign,
  FileText,
  Percent,
  Save,
  Shield,
  Upload,
  User,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import apiService from "../../services/apiService";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "../../components/ui/pagination";
import { useNavigate } from "react-router";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
// import { Separator } from 'docx';
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { Textarea } from "../../components/ui/textarea";
import { useCustomerStore } from "../../stores/customerStore";
import { KPICard } from "../../components/purchaseOrder/KPICard";
interface TermSheet {
  id: string;
  generatedAt: string;
  generatedBy: string;
  ptpm: number;
  disbursementAmount: number;
  margin: number;
  status:
    | "In Review"
    | "Approved"
    | "Rejected"
    | "approved"
    | "rejected"
    | "review";
}
interface ApiResponse<T> {
  data: T;
  downloaded: T;
  message?: string;
  status?: number;
  pagination?: any;
  // financial?:FinancialTabProps
}

interface PaginationData {
  currentPage?: number;
  totalCustomers?: number;
  pageSize?: number;
  totalPages?: number;
}

type FilterType = "all" | "In Review" | "Approved" | "Rejected";

const TermSheetDashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCompanies, setExpandedCompanies] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [CurrentTermSheetId, setCurrentTermSheetId] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [companies, setCompanies] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    currentPage: 1,
    totalCustomers: 0,
    pageSize: 25,
    totalPages: 1,
  });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [remark, setRemark] = useState("");
  const [selectedCalculation, setSelectedCalculation] = useState<any>([]);
  const [dashboardSummary, setDashboardSummary] = useState<{
    total: number;
    inReview: number;
    approved: number;
    rejected: number;
  } | null>(null);

  // Calculate company status based on term sheet statuses
  const getCompanyStatus = (
    termSheets: TermSheet[]
  ): "Draft" | "In Review" | "Approved" | "Rejected" => {
    const statuses = termSheets.map((ts) => String(ts.status));

    if (statuses.includes("Draft") || statuses.includes("draft")) {
      return "Draft";
    }

    if (statuses.includes("Approved") || statuses.includes("approved")) {
      return "Approved";
    }

    if (statuses.includes("In Review") || statuses.includes("review")) {
      return "In Review";
    }

    return "Rejected";
  };

  // const customer = useCustomerStore((s) => s.customer);

  const fetchTermSheetData = async (search = "", status?: string) => {
    if (search == "") {
      setSearchTerm("");
    }
    try {
      setIsLoading(true);

      const query = new URLSearchParams({
        page: paginationData?.currentPage?.toString() || "1",
        pageSize: paginationData?.pageSize?.toString() || "25",
        search,
        sortBy,
        sortOrder,
      });

      if (status && status !== "all") {
        query.append("status", status.toLowerCase()); // backend expects uppercase, adjust if needed
      }

      const response = await apiService.get<ApiResponse<any>>(
        `/customers/get-customer-term-sheet?${query.toString()}`
      );

      setCompanies(response.data);
      setPaginationData(response.pagination);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTermSheetDashboardSummary = async () => {
    try {
      // setIsLoading(true);
      const [data] = await Promise.all([
        apiService.get<ApiResponse<any>>(
          `/customers/get-customer-term-sheet-dashboard-summary`
        ),
      ]);
      if (data?.status === 200) {
        setDashboardSummary(data.data);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTermSheetDashboardSummary();
  }, []);

  useEffect(() => {
    fetchTermSheetData(debouncedSearchTerm);
  }, [debouncedSearchTerm, sortBy, sortOrder, paginationData.currentPage]);

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler); // clear timeout on next keystroke
    };
  }, [searchTerm]);

  const handleViewClick = async (calculation: string) => {
    try {
      setIsLoading(true);
      const [termsheetResponse] = await Promise.all([
        apiService.get<ApiResponse<any>>(
          `/customers/get-by-term-sheet-id/${calculation}`
        ),
      ]);
      setSelectedCalculation(termsheetResponse.data);
      setCurrentTermSheetId(termsheetResponse.data.id);
      setRemark(termsheetResponse.data.remarks || "");
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPaginationData((prev) => ({
      ...prev,
      currentPage: newPage,
    }));
  };

  const handleSearchTerm = (value: string) => {
    setSearchTerm(value); // No fetch here
  };

  const formatLabelRentalCalculationBasis = (key: string) => {
    return key
      ?.replace(/([a-z])([A-Z])/g, "$1 $2") // Insert space before capital letters
      ?.replace(/^./, (s) => s.toUpperCase()) // Capitalize first letter
      ?.replace(/\bGst\b/, "GST") // Optional: force uppercase GST
      ?.replace(/\bTds\b/, "TDS") // Optional: force uppercase TDS
      ?.replace(/\bReview\b/i, "In Review"); // Optional: force uppercase TDS
  };

  // Load saved calculations and term sheets from localStorage on component mount

  const toggleCompanyExpansion = (companyId: string) => {
    setExpandedCompanies((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    );
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      "In Review": "bg-gray-100 text-gray-700 border-gray-200",
      Approved: "bg-green-100 text-green-700 border-green-200",
      Rejected: "bg-red-100 text-red-700 border-red-200",
      rejected: "bg-red-100 text-red-700 border-red-200",
      review: "bg-gray-100 text-gray-700 border-gray-200",
      approved: "bg-green-100 text-green-700 border-green-200",
    };
    return variants[status as keyof typeof variants] || variants["In Review"];
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleRemarkSave = (remark: string) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        await apiService.post<ApiResponse<any>>(
          `/customers/update-term-sheet-remark/${CurrentTermSheetId}`,
          { remark }
        );
      } catch (error) {
        console.error("Failed to update remark:", error);
      }
    }, 800); // adjust debounce delay as needed
  };

  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };
  // Calculate summary statistics

  const summaryCards = [
    {
      title: "Total Term Sheets",
      value: dashboardSummary?.total ?? 0,
      icon: Building2,
      subtitle: "",
      filter: "" as FilterType,
      color: "teal",
    },
    {
      title: "In Review",
      value: dashboardSummary?.inReview ?? 0,
      icon: Calendar,
      subtitle: "",
      filter: "review" as FilterType,
      color: "teal",
    },

    {
      title: "Rejected",
      value: dashboardSummary?.rejected ?? 0,
      icon: CreditCard,
      subtitle: "",
      filter: "Rejected" as FilterType,
      color: "red",
    },

    {
      title: "Approved",
      value: dashboardSummary?.approved ?? 0,
      icon: TrendingUp,
      subtitle: "",
      filter: "Approved" as FilterType,
      color: "teal",
    },

    // {
    //   title: 'Approved',
    //   value: dashboardSummary?.approved ?? 0,
    //   icon: TrendingUp,
    //   subtitle: '',
    //   filter: 'Approved' as FilterType,
    //   color: 'teal'
    // }
  ];

  return (
    <div className="min-h-screen p-2 w-[100%] bg-white">
      {isLoading && (
        <div className="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white p-5 rounded-lg shadow-lg flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#8b7355]"></div>
            <p className="mt-3 text-gray-700 font-medium">Loading data...</p>
          </div>
        </div>
      )}

      <div className="mx-auto p-2">
        {/* Header */}
        {/* <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Term Sheet Dashboard</h1>
          </div>
        </div> */}
        {/* Summary Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 space-y-6">
          {summaryCards.map((card) => (
            <Card
              onClick={() => {
                setActiveFilter(card.filter);
                fetchTermSheetData("", card.filter);
              }}
              key={card.title}
              className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-[#d9cba6]/10 hover:-translate-y-1 ${activeFilter === card.filter
                ? 'bg-gradient-to-br from-[#d9cba6]/20 to-[#d9cba6]/10 border-[#d9cba6]/40 shadow-lg shadow-[#d9cba6]/20'
                : 'bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md border-[#d9cba6]/20'
                }`}
            // onClick={() => setActiveFilter(card.filter)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-700">{card.title}</CardTitle>
                <card.icon className={`h-4 w-4 ${card.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
              </CardContent>
            </Card>
          ))}
        </div> */}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {summaryCards.map((card) => (
            <KPICard
              key={card.title}
              title={card.title}
              count={card.value}
              value={""}
              icon={card.icon}
              variant={card.color as "default" | "gold" | "teal" | "red"}
              onClick={() => {
                setActiveFilter(card.filter);
                fetchTermSheetData("", card.filter);
              }}
            />
          ))}
        </div>

        {/* <div className="flex items-center space-x-4 space-y-6 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8b7355] h-4 w-4" />
            <Input
              placeholder="Search: Company Name, Company ID"
              value={searchTerm}
              onChange={(e) => handleSearchTerm(e.target.value)}
              className="pl-10 bg-white/80 backdrop-blur-sm border-[#d9cba6]/30 focus:border-[#d9cba6]/60 focus:ring-[#d9cba6]/20"
            />
          </div>
        </div> */}

        {/* Search */}
        <div className="relative mt-6 mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by Company Name, Company ID"
            value={searchTerm}
            onChange={(e) => handleSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Enhanced Main Table */}
        <Card className="bg-white/90 backdrop-blur-sm shadow-sm border-[#d9cba6]/20 space-y-6">
          <CardHeader className="border-b border-[#d9cba6]/20 px-6 py-4 bg-gradient-to-r from-[#d9cba6]/5 to-transparent">
            <CardTitle className="text-lg font-semibold text-gray-900">
              Companies & Term Sheets
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-[#d9cba6]/10 to-[#d9cba6]/5 hover:from-[#d9cba6]/15 hover:to-[#d9cba6]/8 border-b border-[#d9cba6]/20">
                  <TableHead className="font-semibold text-[#8b7355] py-4">
                    Company
                  </TableHead>
                  <TableHead
                    onClick={() => handleSortChange("createdAt")}
                    className="cursor-pointer font-semibold text-[#8b7355]"
                  >
                    Created At{" "}
                    {sortBy === "createdAt" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead className="font-semibold text-[#8b7355]">
                    Credit Rating
                  </TableHead>
                  <TableHead className="font-semibold text-[#8b7355]">
                    Industry
                  </TableHead>
                  <TableHead className="font-semibold text-[#8b7355]">
                    RM Name
                  </TableHead>
                  <TableHead
                    onClick={() => handleSortChange("dealSize")}
                    className="cursor-pointer font-semibold text-[#8b7355]"
                  >
                    Program Limit{" "}
                    {sortBy === "dealSize" && (sortOrder === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead className="font-semibold text-[#8b7355]">
                    Status
                  </TableHead>
                  <TableHead className="font-semibold text-[#8b7355] text-center">
                    Term Sheets
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <React.Fragment key={company.id}>
                    <TableRow className="hover:bg-gradient-to-r hover:from-[#d9cba6]/8 hover:to-transparent transition-all duration-200 border-b border-[#d9cba6]/10 group">
                      <TableCell
                        className="py-4 cursor-pointer"
                        onClick={() => {
                          sessionStorage.setItem(
                            "selectedCustomer",
                            JSON.stringify({
                              customerId: company?.id,
                              companyName: company?.name,
                              entityType: company?.entity_type ?? null,
                            })
                          );

                          let company1 = {
                            id: company.id,
                            name: company.name,
                            city: "",
                            cin: "",
                          };
                          useCustomerStore.getState().setCustomer(company1);

                          navigate(`/manage-customer/${company.id}`);
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          {company.logo ? (
                            <img
                              src={company.logo}
                              alt={company.name}
                              className="w-10 h-10 rounded-lg object-cover shadow-sm"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-br from-[#d9cba6] to-[#8b7355] rounded-lg flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                              {company.name
                                ?.split(" ")
                                .slice(0, 2)
                                .map((word: any) => word[0])
                                .join("")
                                .toUpperCase()}
                            </div>
                          )}
                          <span className="font-medium text-gray-900 group-hover:text-[#8b7355] transition-colors">
                            {company.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {formatDate(company.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="bg-[#d9cba6]/10 text-[#8b7355] border-[#d9cba6]/30 font-medium hover:bg-[#d9cba6]/20 transition-colors"
                        >
                          {company.creditRating ?? "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {company.industry}
                      </TableCell>
                      <TableCell className="text-gray-700 font-medium">
                        {company.rmName}
                      </TableCell>
                      <TableCell className="text-gray-700 font-medium">
                        {formatCurrency(company.programLimit)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={getStatusBadge(
                            getCompanyStatus(company.termSheets)
                          )}
                        >
                          {getCompanyStatus(company.termSheets)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCompanyExpansion(company.id)}
                          className="flex items-center space-x-2 hover:bg-[#d9cba6]/15 text-[#8b7355] font-medium transition-all duration-200 hover:shadow-sm"
                        >
                          {expandedCompanies.includes(company.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span>
                            {company.termSheets.length} Term Sheet
                            {company.termSheets.length !== 1 ? "s" : ""}
                          </span>
                        </Button>
                      </TableCell>
                    </TableRow>

                    {/* Enhanced Term Sheets Expandable Section */}
                    {expandedCompanies.includes(company.id) && (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="p-0 bg-gradient-to-r from-[#d9cba6]/5 to-transparent"
                        >
                          <div className="p-6 border-t border-[#d9cba6]/20">
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-[#d9cba6]/20 overflow-hidden shadow-sm">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-gradient-to-r from-[#d9cba6]/8 to-[#d9cba6]/4 hover:from-[#d9cba6]/12 hover:to-[#d9cba6]/6">
                                    <TableHead className="font-semibold text-[#8b7355]">
                                      Term Sheet ID
                                    </TableHead>
                                    <TableHead className="font-semibold text-[#8b7355]">
                                      Generated At
                                    </TableHead>
                                    <TableHead className="font-semibold text-[#8b7355]">
                                      Generated By
                                    </TableHead>
                                    <TableHead className="font-semibold text-[#8b7355]">
                                      PTPM
                                    </TableHead>
                                    <TableHead className="font-semibold text-[#8b7355]">
                                      Disbursement Amount
                                    </TableHead>
                                    <TableHead className="font-semibold text-[#8b7355]">
                                      Margin (%)
                                    </TableHead>
                                    <TableHead className="font-semibold text-[#8b7355]">
                                      Status
                                    </TableHead>
                                    <TableHead className="font-semibold text-[#8b7355]">
                                      Actions
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {company.termSheets.map((termSheet: any) => (
                                    <TableRow
                                      key={termSheet.id}
                                      className="hover:bg-gradient-to-r hover:from-[#d9cba6]/6 hover:to-transparent transition-all duration-200 group"
                                    >
                                      <TableCell className="font-semibold text-[#8b7355] group-hover:text-[#8b7355]/80 transition-colors">
                                        {termSheet.id}
                                      </TableCell>
                                      <TableCell className="text-gray-700">
                                        {formatDate(termSheet.generatedAt)}
                                      </TableCell>
                                      <TableCell className="text-gray-700 font-medium">
                                        {termSheet.generatedBy}
                                      </TableCell>
                                      <TableCell className="text-gray-700 font-medium">
                                        {termSheet.ptpm
                                          ? termSheet.ptpm.toFixed(2)
                                          : "-"}
                                      </TableCell>
                                      <TableCell className="text-gray-700 font-medium">
                                        {formatCurrency(
                                          termSheet.disbursementAmount
                                        )}
                                      </TableCell>
                                      <TableCell className="text-gray-700 font-medium">
                                        {termSheet.margin
                                          ? termSheet.margin.toFixed(2)
                                          : "-"}
                                        %
                                      </TableCell>
                                      <TableCell>
                                        <Badge
                                          className={getStatusBadge(
                                            termSheet.status
                                          )}
                                        >
                                          {formatLabelRentalCalculationBasis(
                                            termSheet.status
                                          )}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-center">
                                        <div className="flex items-center gap-1 ">
                                          <Sheet>
                                            <SheetTrigger asChild>
                                              <div className="flex justify-center space-x-2 ">
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="hover:bg-[#d9cba6]/15 hover:border-[#d9cba6]/40 text-[#8b7355] hover:text-[#8b7355]/80 transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-[#d9cba6]/20"
                                                  onClick={() =>
                                                    handleViewClick(
                                                      termSheet.id
                                                    )
                                                  }
                                                >
                                                  <Eye className="h-3 w-3 mr-1" />
                                                  View
                                                </Button>
                                              </div>
                                            </SheetTrigger>
                                            <SheetContent className="w-[600px] sm:w-[800px] overflow-y-auto">
                                              <SheetHeader className="pb-6">
                                                <SheetTitle className="flex items-center gap-3 text-xl">
                                                  <div className="p-2 bg-[#f8f6f1] rounded-lg">
                                                    <FileText className="h-6 w-6 text-[#9e7926]" />
                                                  </div>
                                                  <div>
                                                    <div className="text-xl font-bold text-gray-900">
                                                      Term Sheet Details
                                                    </div>
                                                    <div className="text-sm text-gray-500 font-mono">
                                                      {selectedCalculation
                                                        ? selectedCalculation?.termSheetId
                                                        : ""}
                                                    </div>
                                                  </div>
                                                </SheetTitle>
                                              </SheetHeader>
                                              {isLoading ? (
                                                // Loader UI
                                                <div className="flex flex-col items-center justify-center py-12">
                                                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#d9cba6] border-t-transparent" />
                                                  <p className="mt-4 text-gray-700 text-sm">
                                                    Loading calculation...
                                                  </p>
                                                </div>
                                              ) : selectedCalculation ? (
                                                // Main content once data is available
                                                <div className="space-y-8">
                                                  {/* Basic Information */}
                                                  <div className="bg-[#f8f6f1] rounded-lg p-6">
                                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                      <User className="h-5 w-5 text-[#9e7926]" />
                                                      Basic Information
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-6">
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <FileText className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Calculation ID
                                                          </Label>
                                                          <div className="font-mono font-semibold text-gray-900">
                                                            {
                                                              selectedCalculation.calculationId
                                                            }
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <FileText className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Termsheet ID
                                                          </Label>
                                                          <div className="font-mono font-semibold text-gray-900">
                                                            {
                                                              selectedCalculation.termSheetId
                                                            }
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Calendar className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Generated at
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {formatDate(
                                                              selectedCalculation.downloadedAt
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <User className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Generated by
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {
                                                              selectedCalculation.downloadedBy
                                                            }
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Asset & GST Information */}
                                                  <div className="bg-[#f8f6f1] rounded-lg p-6">
                                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                      <Building2 className="h-5 w-5 text-[#9e7926]" />
                                                      Asset & Tax Information
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-6">
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <DollarSign className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Asset Value
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {formatCurrency(
                                                              selectedCalculation
                                                                ?.inputs
                                                                ?.totalRental
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Percent className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            GST Rate
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {(
                                                              selectedCalculation
                                                                ?.inputs
                                                                ?.gstRate * 100
                                                            ).toFixed(0)}
                                                            %
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Rental Terms */}
                                                  <div className="bg-[#f8f6f1] rounded-lg p-6">
                                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                      <Clock className="h-5 w-5 text-[#9e7926]" />
                                                      Rental Terms
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-6">
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Clock className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Frequency
                                                          </Label>
                                                          <div className="font-semibold text-gray-900 capitalize">
                                                            {
                                                              selectedCalculation?.inputs?.rentalFrequency
                                                             ? selectedCalculation?.inputs?.rentalFrequency
                                                             : '-'
                                                            }
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Calendar className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Tenure
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {
                                                              selectedCalculation?.inputs?.totalMonths 
                                                                ? selectedCalculation?.inputs?.totalMonths
                                                                : '-'
                                                            }{" "}
                                                            Months
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <DollarSign className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            PTPM Consideration
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {selectedCalculation?.inputs?.ptpmConsideration?.toFixed(
                                                              2
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>

                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <DollarSign className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Final PTPM
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {selectedCalculation?.inputs?.finalPTPM?.toFixed(
                                                              2
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Percent className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            GST Passback
                                                          </Label>
                                                          <Badge
                                                            variant={
                                                              selectedCalculation?.inputs?.gstPassbackEnabled
                                                                ? "default"
                                                                : "secondary"
                                                            }
                                                            className="ml-2"
                                                          >
                                                            {selectedCalculation?.inputs?.gstPassbackEnabled
                                                              ? "Enabled"
                                                              : "Disabled"}
                                                          </Badge>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Percent className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Discounting Factor
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {(
                                                              selectedCalculation?.inputs?.discountingFactor *
                                                              100
                                                            ).toFixed(2)}
                                                            %
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Clock className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Rental Calculation
                                                            Basis
                                                          </Label>
                                                          <div className="font-semibold text-gray-900 capitalize">
                                                            {formatLabelRentalCalculationBasis(
                                                              selectedCalculation?.inputs?.rentalCalculationBasis
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Financial Details */}
                                                  <div className="bg-[#f8f6f1] rounded-lg p-6">
                                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                      <CreditCard className="h-5 w-5 text-[#9e7926]" />
                                                      Financial Details
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-6">
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Shield className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Security Deposit
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {formatCurrency(
                                                              selectedCalculation
                                                                ?.summary
                                                                ?.securityDeposit
                                                            )}{" "}
                                                            (
                                                            {
                                                              selectedCalculation
                                                                ?.inputs
                                                                ?.securityDepositPercentage
                                                            }
                                                            %){" "}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <CreditCard className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Processing Fee
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {formatCurrency(
                                                              selectedCalculation
                                                                ?.summary
                                                                ?.processingFee
                                                            )}{" "}
                                                            (
                                                            {
                                                              selectedCalculation
                                                                ?.inputs
                                                                ?.processingFeePercentage
                                                            }
                                                            %){" "}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Banknote className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Residual Value
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {formatCurrency(
                                                              selectedCalculation
                                                                ?.inputs
                                                                ?.refundableSecurity ||
                                                                0
                                                            )}{" "}
                                                            (
                                                            {
                                                              selectedCalculation
                                                                ?.inputs
                                                                ?.refundableSecurityPercentage
                                                            }
                                                            %){" "}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Summary Information */}
                                                  <div className="bg-[#f8f6f1] rounded-lg p-6">
                                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                      <DollarSign className="h-5 w-5 text-[#9e7926]" />
                                                      Summary Information
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-6">
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <DollarSign className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Disbursement Amount
                                                          </Label>
                                                          <div className="font-semibold text-gray-900">
                                                            {formatCurrency(
                                                              selectedCalculation
                                                                ?.inputs
                                                                ?.disbursementAmount ||
                                                                0
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <DollarSign className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Margin
                                                          </Label>
                                                          <div
                                                            className={`font-semibold ${
                                                              selectedCalculation
                                                                ?.summary
                                                                ?.isNegativeMargin
                                                                ? "text-red-600"
                                                                : "text-green-600"
                                                            }`}
                                                          >
                                                            {formatCurrency(
                                                              selectedCalculation
                                                                ?.summary
                                                                ?.margin_available
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="col-span-2 flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                                          <Percent className="h-4 w-4 text-gray-600" />
                                                        </div>
                                                        <div>
                                                          <Label className="text-sm text-gray-600">
                                                            Margin Percentage
                                                          </Label>
                                                          <div
                                                            className={`font-semibold ${
                                                              selectedCalculation
                                                                ?.summary
                                                                ?.isNegativeMargin
                                                                ? "text-red-600"
                                                                : "text-green-600"
                                                            }`}
                                                          >
                                                            {Number(selectedCalculation?.summary?.margin_percentage)?.toFixed(
                                                              2
                                                            )}
                                                            %
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>

                                                  <Separator />

                                                  {/* Remark Section */}
                                                  <div className="bg-gray-50 rounded-lg p-6">
                                                    <div className="flex items-center gap-2 mb-4">
                                                      <FileText className="h-5 w-5 text-gray-600" />
                                                      <Label className="text-lg font-semibold text-gray-900">
                                                        Remark
                                                      </Label>
                                                    </div>
                                                    <div className="flex gap-3">
                                                      <Textarea
                                                        value={remark}
                                                        onChange={(e) => {
                                                          const value =
                                                            e.target.value;
                                                          setRemark(value); // ✅ Update local state so it shows what you type
                                                          handleRemarkSave(
                                                            value
                                                          ); // ✅ Save with debounce
                                                        }}
                                                        placeholder="Add your remark here..."
                                                        className="flex-1 h-[100px]"
                                                      />
                                                      {/* <Button onClick={handleSaveRemark} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
                                                        <Save className="h-4 w-4" />
                                                        Save
                                                      </Button> */}
                                                    </div>
                                                  </div>
                                                </div>
                                              ) : (
                                                // Fallback if no data returned
                                                <div className="p-4 text-center text-gray-500 text-sm">
                                                  No data found for this
                                                  calculation.
                                                </div>
                                              )}
                                            </SheetContent>
                                          </Sheet>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>

          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <div className="text-sm text-gray-500 font-['Poppins',Helvetica]">
              {paginationData?.totalCustomers === 0 ? (
                "Showing 0 of 0 entries"
              ) : (
                <>
                  Showing{" "}
                  {(paginationData?.currentPage - 1) *
                    paginationData?.pageSize +
                    1}{" "}
                  to{" "}
                  {Math.min(
                    paginationData?.currentPage * paginationData?.pageSize,
                    paginationData?.totalCustomers
                  )}{" "}
                  of {paginationData?.totalCustomers} entries
                </>
              )}
            </div>

            <div className="ml-auto">
              <Pagination>
                <PaginationContent>
                  {/* Previous Button */}
                  <PaginationItem>
                    <PaginationPrevious
                      className="border-[#3f8b98] text-[#3f8b98] border rounded-lg h-[34px] w-24 font-['Poppins',Helvetica] font-normal"
                      onClick={() =>
                        paginationData?.currentPage > 1 &&
                        handlePageChange(paginationData?.currentPage - 1)
                      }
                    >
                      Previous
                    </PaginationPrevious>
                  </PaginationItem>

                  {/* Page Numbers */}
                  {Array.from(
                    { length: paginationData?.totalPages },
                    (_, index) => (
                      <PaginationItem key={index}>
                        <PaginationLink
                          className={`rounded-lg h-8 w-[30px] ${
                            paginationData?.currentPage === index + 1
                              ? "bg-[#3f8b98] text-white" // Active page styling
                              : "border-[#3f8b98] text-[#3f8b98] border" // Inactive page styling
                          } font-['Poppins',Helvetica] font-normal`}
                          onClick={() => handlePageChange(index + 1)}
                        >
                          <span className="text-red"> {index + 1}</span>
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}

                  {/* Next Button */}
                  <PaginationItem>
                    <PaginationNext
                      className="border-[#3f8b98] text-[#3f8b98] border rounded-lg h-[34px] w-16 font-['Poppins',Helvetica] font-normal"
                      onClick={() =>
                        paginationData?.currentPage <
                          paginationData?.totalPages &&
                        handlePageChange(paginationData?.currentPage + 1)
                      }
                    >
                      Next
                    </PaginationNext>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermSheetDashboard;
